import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  complaintAction,
  createComplaint,
  deleteComplaint,
  fetchComplaintDetail,
  fetchComplaints,
  proposeComplaintResolution,
  updateComplaintDraft,
  uploadComplaintAttachment,
} from "@/api/complaints";
import type {
  ComplaintCreatePayload,
  ComplaintDraftUpdatePayload,
  ComplaintListQuery,
  ComplaintProposePayload,
} from "@/api/types";

const LIST_STALE = 30_000;
const DETAIL_STALE = 30_000;

/** F5 — invalidate after every mutation (mirrors invalidateF4). */
export function invalidateF5(
  qc: ReturnType<typeof useQueryClient>,
  complaintId?: string | null,
) {
  qc.invalidateQueries({ queryKey: ["complaints", "list"] });
  qc.invalidateQueries({ queryKey: ["homeSummary"] });
  if (complaintId) {
    qc.invalidateQueries({ queryKey: ["complaints", "detail", complaintId] });
  }
}

export function useComplaintList(q: ComplaintListQuery = {}) {
  const key = [
    "complaints",
    "list",
    q.view ?? "all",
    q.scope ?? "team",
    q.search ?? "",
    q.cursor ?? null,
  ] as const;
  return useQuery({
    queryKey: key,
    queryFn: ({ signal }) => fetchComplaints(q, signal),
    staleTime: LIST_STALE,
  });
}

export function useComplaintDetail(id: string | null) {
  return useQuery({
    queryKey: ["complaints", "detail", id],
    queryFn: ({ signal }) => fetchComplaintDetail(id!, signal),
    enabled: !!id,
    staleTime: DETAIL_STALE,
  });
}

export function useCreateComplaint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ComplaintCreatePayload) => createComplaint(body),
    onSuccess: (data) => invalidateF5(qc, data.id),
  });
}

/** COMP-S3 W4c — partial draft update (PUT). 409 conflict surfaces to the caller. */
export function useUpdateComplaintDraft(complaintId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ComplaintDraftUpdatePayload) => updateComplaintDraft(complaintId!, body),
    onSuccess: (data) => invalidateF5(qc, data.id),
  });
}

/** COMP-S3-FIX W4 — author deletes own draft. Invalidates the list (row disappears). */
export function useDeleteComplaint(complaintId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => deleteComplaint(complaintId!),
    onSuccess: () => invalidateF5(qc, complaintId),
  });
}

/** One mutation hook for every body-carrying transition. */
export function useComplaintAction(complaintId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ action, body }: { action: string; body?: Record<string, unknown> }) =>
      complaintAction(complaintId!, action, body ?? {}),
    onSuccess: () => invalidateF5(qc, complaintId),
  });
}

export function useProposeResolution(complaintId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ComplaintProposePayload) => proposeComplaintResolution(complaintId!, body),
    onSuccess: () => invalidateF5(qc, complaintId),
  });
}

export function useUploadComplaintAttachment(complaintId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { fileId: string; attachmentType: string; notes?: string }) =>
      uploadComplaintAttachment(complaintId!, body),
    onSuccess: () => invalidateF5(qc, complaintId),
  });
}
